import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';

import { ApiService } from './api.service';
import { DashboardLayoutService } from './dashboard-layout.service';
import { ToasterService } from './toaster.service';

export interface CustomDashboardPinToggleParams {
  /** Backend `pages.id` for the source report page. */
  pageId: number;
  /** Catalog `widget_key` (same as section id enum value). */
  widgetKey: string;
  colSpan?: number | null;
  /**
   * Forwarded verbatim as `widgets[].custom_config`. Lets pages persist
   * per-widget UI state alongside the placement so the embedded copy on
   * My Custom Dashboard can rehydrate it (e.g. SEO Overview saves the
   * currently-selected platform `view` here — `metaData` / `googleAdsSummary`
   * — so the pinned card renders the same Meta/Google Ads data the user
   * was looking at when they hit "Pin to Custom Dashboard").
   */
  customConfig?: Record<string, any> | null;
  isCurrentlyPinned: boolean;
  onPinnedChange: (pinned: boolean) => void;
  onPendingChange: (pending: boolean) => void;
}

/**
 * Params for toggling a card whose numeric catalog `widgets.id` is already
 * known (e.g. pages built on `DashboardPageEngine` — Monthly Report 1 / 2).
 *
 * Unlike {@link CustomDashboardPinToggleParams} this skips the catalog
 * `widget_key` → `widgets.id` lookup because the layout already carries the
 * resolved id.
 */
export interface CustomDashboardLayoutToggleParams {
  /** Backend `pages.id` for the source report page. */
  pageId: number;
  /** Catalog `widgets.id` (already resolved by the layout engine). */
  widgetId: number;
  /** Source page id used when seeding a brand-new mixed dashboard. */
  sourcePageId?: number | null;
  colSpan?: number | null;
  rowSpan?: number | null;
  // Widened to `Record<string, any>` so structural types like
  // `DashboardWidgetCustomConfig` (no string index signature) are
  // assignable. The shape is forwarded verbatim to the backend's
  // `widget_ids[].custom_config`.
  customConfig?: Record<string, any> | null;
  /**
   * Optional product id override. When omitted the service resolves it
   * via {@link ApiService} (sidebar pid → cuid → product list lookup).
   * Engine-based pages typically pass the same fallback chain they use
   * for `GET /dashboards`.
   */
  productId?: string | number | null;
  isCurrentlyPinned: boolean;
  onPinnedChange: (pinned: boolean) => void;
  onPendingChange: (pending: boolean) => void;
}

/**
 * Shared add/remove/hydrate logic for "Pin to Custom Dashboard" on report pages
 * that are not yet on {@link DashboardPageEngine}. Mirrors the flow used by
 * Monthly Report 1 / 2 (`toggleVisibility` + `createDashboard` mixed seed).
 */
@Injectable({ providedIn: 'root' })
export class CustomDashboardPinService {
  private readonly layoutService = inject(DashboardLayoutService);
  private readonly apiService = inject(ApiService);
  private readonly toaster = inject(ToasterService);

  /** pageId → widget_key → catalog `widgets.id` */
  private readonly widgetIdByPageAndKey = new Map<number, Map<string, number>>();

  /**
   * Loads visible widget_keys from the user's mixed dashboard for the active
   * product so standalone pages can show accurate "Added" badges after refresh.
   *
   * Backend `widget_key`s are product-prefixed (e.g. `de_sp_overall_stats`,
   * `co_sp_overall_stats`). To make frontend section enums product-agnostic,
   * we emit BOTH the raw backend key AND the canonical suffix-stripped
   * variant (e.g. `sp_overall_stats`). Components can use either when
   * comparing against their `SectionId` enum values.
   */
  hydratePinnedWidgetKeys(pageId: number): Observable<Set<string>> {
    const productId = this.resolveProductId();
    if (productId == null) return of(new Set<string>());

    return this.layoutService.load(pageId, 'web', true, productId).pipe(
      map((res): Set<string> => {
        const keys = new Set<string>();
        (res?.widgets ?? []).forEach((w: any) => {
          if (w?.widget_key && w.is_visible !== false) {
            const raw = String(w.widget_key);
            keys.add(raw);
            const canonical = stripProductPrefix(raw);
            if (canonical !== raw) keys.add(canonical);
          }
        });
        return keys;
      }),
      catchError((): Observable<Set<string>> => of(new Set<string>())),
    );
  }

  /**
   * Engine-page variant of {@link hydratePinnedWidgetKeys}. Returns the
   * set of card ids that are currently visible on the user's mixed
   * ("Custom") dashboard for the given page, translating each backend
   * `widget_key` to the template card id via the caller-supplied
   * `resolveCardId` (typically the same resolver passed to
   * `DashboardEngineConfig.resolveCardId`).
   *
   * Used by engine pages (e.g. Monthly Report 1) to mark cards with the
   * green "Added" pill after a refresh without duplicating the
   * `GET /dashboards?...&mixed=true&product_id=<id>` request logic in
   * every component.
   *
   * @param pageId         Backend `pages.id` for the source report.
   * @param resolveCardId  Maps a raw API `widget_key` to a template card id
   *                       (or null when the widget doesn't belong to this page).
   * @param productId      Optional product id override. When omitted the
   *                       service falls back to {@link resolveProductId}.
   * @returns Set of card ids currently pinned & visible on the mixed dashboard.
   */
  hydrateAddedCardIds<TCardId extends string>(
    pageId: number,
    resolveCardId: (widgetKey: string) => TCardId | null,
    productId?: string | number | null,
  ): Observable<Set<TCardId>> {
    const pid = productId ?? this.resolveProductId();
    if (pid == null) return of(new Set<TCardId>());

    return this.layoutService.load(pageId, 'web', true, pid).pipe(
      map((res): Set<TCardId> => {
        const ids = new Set<TCardId>();
        (res?.widgets ?? []).forEach((w: any) => {
          if (!w?.widget_key || w.is_visible === false) return;
          const cardId = resolveCardId(String(w.widget_key));
          if (cardId != null) ids.add(cardId);
        });
        return ids;
      }),
      catchError((): Observable<Set<TCardId>> => of(new Set<TCardId>())),
    );
  }

  /**
   * Batch-resolves every catalog `widgets.id` registered on `pageId`, keyed
   * by the *canonical* (suffix-stripped) `widget_key`.
   *
   * Standalone report pages (Social Platform / SEO Stats / etc.) use this
   * to:
   *   1. Decide which sections are pinnable (only sections whose canonical
   *      enum value appears in the returned map can be added — those that
   *      don't have a backend catalog entry are silently hidden by the
   *      template, avoiding the "Widget X is not registered" toast.)
   *   2. Pass a pre-resolved numeric `widgets.id` into
   *      {@link toggleLayoutItem} so the add/remove flow mirrors the
   *      Monthly Report 1 / 2 path exactly (no per-click catalog
   *      round-trip; one POST/PATCH per click; only the clicked widget is
   *      sent to the backend).
   *
   * The map keys are canonical (`stripProductPrefix` applied) so the
   * caller can compare directly against its `SectionId` enum values; the
   * raw product-prefixed key is also added under its original spelling
   * for callers that need it (e.g. when comparing against the raw
   * `widget_key`s emitted by `GET /dashboards`).
   */
  resolveCatalogWidgetIdsForPage(
    pageId: number,
    expectedProductId?: string | number | null,
  ): Observable<Map<string, number>> {
    return this.layoutService.getCatalogProducts().pipe(
      map((catalog): Map<string, number> => {
        const resolved = new Map<string, number>();

        const scopedCatalog =
          expectedProductId != null && String(expectedProductId).trim() !== ''
            ? (catalog as any[]).filter(
                (p: any) => String(p?.id) === String(expectedProductId),
              )
            : (catalog as any[]);
        const effectiveCatalog =
          scopedCatalog.length > 0 ? scopedCatalog : (catalog as any[]);

        for (const product of effectiveCatalog ?? []) {
          for (const page of product?.pages ?? []) {
            if (Number(page.id) !== pageId) continue;
            for (const w of page?.widgets ?? []) {
              const id = Number(w?.id);
              if (!Number.isFinite(id) || id <= 0) continue;
              const raw = String(w?.widget_key ?? '');
              if (!raw) continue;
              resolved.set(raw, id);
              const canonical = stripProductPrefix(raw);
              if (canonical !== raw && !resolved.has(canonical)) {
                resolved.set(canonical, id);
                this.cacheWidgetId(pageId, canonical, id);
              }
              this.cacheWidgetId(pageId, raw, id);
            }
          }
        }

        return resolved;
      }),
      catchError((): Observable<Map<string, number>> =>
        of(new Map<string, number>()),
      ),
    );
  }

  /**
   * Add/remove a card on the user's mixed ("Custom") dashboard for pages
   * built on {@link DashboardPageEngine}. The numeric `widgetId` is already
   * known to the engine, so we skip the catalog lookup performed by
   * {@link toggleSection}.
   *
   * Flow mirrors `addToCustomDashboard` / `_removeFromCustomDashboard` that
   * previously lived inside the Monthly Report 1 / 2 components:
   *   • Add → probe mixed dashboard, then either toggle visibility ON or
   *     `POST /dashboards` once (mixed seed) when none exists yet.
   *   • Remove → toggle visibility OFF on the existing mixed dashboard, or
   *     just clear local state when no mixed dashboard exists.
   */
  toggleLayoutItem(params: CustomDashboardLayoutToggleParams): void {
    const {
      pageId,
      widgetId,
      sourcePageId,
      colSpan,
      rowSpan,
      customConfig,
      productId: explicitProductId,
      isCurrentlyPinned,
      onPinnedChange,
      onPendingChange,
    } = params;

    if (!widgetId || widgetId <= 0) return;

    const productId =
      explicitProductId !== undefined ? explicitProductId : this.resolveProductId();

    onPendingChange(true);

    if (isCurrentlyPinned) {
      this.removeWidgetById(pageId, productId, widgetId, onPinnedChange, onPendingChange);
      return;
    }

    this.addWidgetById(
      pageId,
      productId,
      widgetId,
      sourcePageId ?? pageId,
      colSpan ?? null,
      rowSpan ?? null,
      customConfig ?? null,
      onPinnedChange,
      onPendingChange,
    );
  }

  toggleSection(params: CustomDashboardPinToggleParams): void {
    const {
      pageId,
      widgetKey,
      colSpan,
      customConfig,
      isCurrentlyPinned,
      onPinnedChange,
      onPendingChange,
    } = params;

    const productId = this.resolveProductId();
    if (productId == null) {
      this.toaster.error('Select a product before pinning sections.');
      return;
    }

    onPendingChange(true);

    if (isCurrentlyPinned) {
      this.removeWidget(pageId, productId, widgetKey, onPinnedChange, onPendingChange);
      return;
    }

    this.addWidget(
      pageId,
      productId,
      widgetKey,
      colSpan ?? 12,
      customConfig ?? null,
      onPinnedChange,
      onPendingChange,
    );
  }

  private addWidget(
    pageId: number,
    productId: string | number,
    widgetKey: string,
    colSpan: number,
    customConfig: Record<string, any> | null,
    onPinnedChange: (pinned: boolean) => void,
    onPendingChange: (pending: boolean) => void,
  ): void {
    this.resolveCatalogWidgetId(pageId, widgetKey, productId)
      .pipe(
        switchMap((widgetId) => {
          if (widgetId == null) {
            this.toaster.error(
              `Widget "${widgetKey}" is not registered in the dashboards catalog. See console for details.`,
            );
            return of(null);
          }

          return this.layoutService.load(pageId, 'web', true, productId).pipe(
            switchMap((mixedRes) => {
              const mixedDashId =
                mixedRes?.dashboard?.id != null ? Number(mixedRes.dashboard.id) : null;
              const alreadyExists = !!mixedRes?.widgets?.some(
                (w: any) =>
                  (w.widget_key === widgetKey || Number(w.widget_id) === widgetId) &&
                  w.is_visible !== false,
              );

              if (mixedDashId && alreadyExists) {
                onPinnedChange(true);
                this.toaster.info('This section is already on your Custom Dashboard.');
                return of(true);
              }

              if (mixedDashId) {
                // return this.layoutService.toggleVisibility(mixedDashId, widgetId).pipe(
                //   map(() => {
                //     onPinnedChange(true);
                //     this.toaster.success('Added to your Custom Dashboard.');
                //     return true;
                //   }),
                // );
                // payload added in toggleVisibility
                return this.layoutService
                  .toggleVisibility(mixedDashId, widgetId, {
                    col_span: colSpan ?? 12,
                    row_span: 1,
                    is_visible: true,
                    custom_config: (customConfig ?? {}) as Record<string, any>,
                  })
                  .pipe(
                    map(() => {
                      onPinnedChange(true);
                      this.toaster.success('Added to your Custom Dashboard.');
                      return true;
                    }),
                  );
              }

              return this.layoutService
                .createDashboard({
                  name: 'My Dashboard',
                  type: 'mixed',
                  platform: 'web',
                  page_id: null,
                  product_id: productId,
                  widget_ids: [
                    {
                      widget_id: widgetId,
                      source_page_id: pageId,
                      col_span: colSpan,
                      row_span: 1,
                      custom_config: (customConfig ?? {}) as Record<string, any>,
                    },
                  ],
                })
                .pipe(
                  map((res) => {
                    if (res) {
                      onPinnedChange(true);
                      this.toaster.success('Custom Dashboard created with this section.');
                      return true;
                    }
                    this.toaster.error('Could not add this section. Please try again.');
                    return null;
                  }),
                );
            }),
          );
        }),
        catchError(() => {
          this.toaster.error('Could not add this section. Please try again.');
          return of(null);
        }),
        finalize(() => onPendingChange(false)),
      )
      .subscribe();
  }

  private removeWidget(
    pageId: number,
    productId: string | number,
    widgetKey: string,
    onPinnedChange: (pinned: boolean) => void,
    onPendingChange: (pending: boolean) => void,
  ): void {
    this.resolveCatalogWidgetId(pageId, widgetKey, productId)
      .pipe(
        switchMap((widgetId) => {
          if (widgetId == null) {
            onPinnedChange(false);
            return of(null);
          }

          return this.layoutService.load(pageId, 'web', true, productId).pipe(
            switchMap((mixedRes) => {
              const mixedDashId =
                mixedRes?.dashboard?.id != null ? Number(mixedRes.dashboard.id) : null;

              if (!mixedDashId) {
                onPinnedChange(false);
                return of(null);
              }

              // return this.layoutService.toggleVisibility(mixedDashId, widgetId).pipe(
              //   map(() => {
              //     onPinnedChange(false);
              //     this.toaster.success('Removed from your Custom Dashboard.');
              //     return true;
              //   }),
              // );
              // payload added in toggleVisibility
              return this.layoutService
                .toggleVisibility(mixedDashId, widgetId, {
                  col_span: 12,
                  row_span: 1,
                  is_visible: false,
                  custom_config: {},
                })
                .pipe(
                  map(() => {
                    onPinnedChange(false);
                    this.toaster.success('Removed from your Custom Dashboard.');
                    return true;
                  }),
                );
            }),
          );
        }),
        catchError(() => {
          this.toaster.error('Could not remove this section. Please try again.');
          return of(null);
        }),
        finalize(() => onPendingChange(false)),
      )
      .subscribe();
  }

  /**
   * Engine-page variant of {@link addWidget}. Skips the catalog lookup
   * because the caller already has the numeric `widgets.id`.
   *
   * Branches by current placement state:
   *   • Mixed dashboard + widget already placed + visible
   *       → no-op; toast "already on your Custom Dashboard".
   *   • Mixed dashboard + widget placed but hidden
   *       → `PATCH /dashboards/<id>/widgets/<wid>/visibility` (preserves
   *         the user's previously-saved `col_span` / `row_span`).
   *   • Mixed dashboard + widget never placed
   *       → `POST /dashboards/<id>/widgets` with explicit
   *         `col_span` / `row_span` / `custom_config` so a designed
   *         default width is not silently widened to the backend default.
   *   • No mixed dashboard yet
   *       → `POST /dashboards` seeded with this widget.
   */
  private addWidgetById(
    pageId: number,
    productId: string | number | null,
    widgetId: number,
    sourcePageId: number | null,
    colSpan: number | null,
    rowSpan: number | null,
    customConfig: Record<string, any> | null,
    onPinnedChange: (pinned: boolean) => void,
    onPendingChange: (pending: boolean) => void,
  ): void {
    this.layoutService
      .load(pageId, 'web', true, productId)
      .pipe(
        switchMap((mixedRes) => {
          const mixedDashId =
            mixedRes?.dashboard?.id != null ? Number(mixedRes.dashboard.id) : null;
          const existingPlacement = mixedRes?.widgets?.find(
            (w: any) => Number(w.widget_id) === widgetId,
          );
          const alreadyVisible =
            !!existingPlacement && (existingPlacement as any).is_visible !== false;

          if (mixedDashId && alreadyVisible) {
            onPinnedChange(true);
            this.toaster.info('This section is already on your Custom Dashboard.');
            return of(true);
          }

          // Placement exists but is hidden → toggleVisibility (PATCH).
          // Avoids overwriting the persisted col_span the user may have
          // resized previously.
          if (mixedDashId && existingPlacement) {
            // return this.layoutService.toggleVisibility(mixedDashId, widgetId).pipe(
            //   map(() => {
            //     onPinnedChange(true);
            //     this.toaster.success('Added to your Custom Dashboard.');
            //     return true;
            //   }),
            //   catchError(() => {
            //     this.toaster.error('Could not add this section. Please try again.');
            //     return of(null);
            //   }),
            // );
            // payload added in toggleVisibility
            return this.layoutService
              .toggleVisibility(mixedDashId, widgetId, {
                col_span: colSpan ?? 12,
                row_span: rowSpan ?? 1,
                is_visible: true,
                custom_config: (customConfig ?? {}) as Record<string, any>,
              })
              .pipe(
                map(() => {
                  onPinnedChange(true);
                  this.toaster.success('Added to your Custom Dashboard.');
                  return true;
                }),
                catchError(() => {
                  this.toaster.error('Could not add this section. Please try again.');
                  return of(null);
                }),
              );
          }

          // Dashboard exists but widget has never been placed → POST
          // /dashboards/<id>/widgets with an explicit col_span so designed
          // widths (e.g. size 6) are not silently widened to the backend
          // default placement size.
          if (mixedDashId) {
            return this.layoutService
              .addWidget(mixedDashId, {
                widget_id: widgetId,
                source_page_id: sourcePageId ?? pageId,
                col_span: colSpan,
                row_span: rowSpan,
                custom_config: customConfig,
              })
              .pipe(
                map((res) => {
                  if (res) {
                    onPinnedChange(true);
                    this.toaster.success('Added to your Custom Dashboard.');
                    return true;
                  }
                  this.toaster.error('Could not add this section. Please try again.');
                  return null;
                }),
                catchError(() => {
                  this.toaster.error('Could not add this section. Please try again.');
                  return of(null);
                }),
              );
          }

          return this.layoutService
            .createDashboard({
              name: 'My Dashboard',
              type: 'mixed',
              platform: 'web',
              page_id: null,
              product_id: productId,
              widget_ids: [
                {
                  widget_id: widgetId,
                  source_page_id: sourcePageId,
                  col_span: colSpan,
                  row_span: rowSpan,
                  custom_config: customConfig,
                },
              ],
            })
            .pipe(
              map((res) => {
                if (res) {
                  onPinnedChange(true);
                  this.toaster.success('Custom Dashboard created with this section.');
                  return true;
                }
                this.toaster.error('Could not add this section. Please try again.');
                return null;
              }),
              catchError(() => {
                this.toaster.error('Could not add this section. Please try again.');
                return of(null);
              }),
            );
        }),
        catchError(() => of(null)),
        finalize(() => onPendingChange(false)),
      )
      .subscribe();
  }

  /**
   * Engine-page variant of {@link removeWidget}. Skips the catalog lookup
   * because the caller already has the numeric `widgets.id`.
   */
  private removeWidgetById(
    pageId: number,
    productId: string | number | null,
    widgetId: number,
    onPinnedChange: (pinned: boolean) => void,
    onPendingChange: (pending: boolean) => void,
  ): void {
    this.layoutService
      .load(pageId, 'web', true, productId)
      .pipe(
        switchMap((mixedRes) => {
          const mixedDashId =
            mixedRes?.dashboard?.id != null ? Number(mixedRes.dashboard.id) : null;

          if (!mixedDashId) { 
            onPinnedChange(false);
            return of(null);
          }

          // return this.layoutService.toggleVisibility(mixedDashId, widgetId).pipe(
          //   map(() => {
          //     onPinnedChange(false);
          //     this.toaster.success('Removed from your Custom Dashboard.');
          //     return true;
          //   }),
          //   catchError(() => {
          //     this.toaster.error('Could not remove this section. Please try again.');
          //     return of(null);
          //   }),
          // );
          // payload added in toggleVisibility
          return this.layoutService
            .toggleVisibility(mixedDashId, widgetId, {
              col_span: 12,
              row_span: 1,
              is_visible: false,
              custom_config: {},
            })
            .pipe(
              map(() => {
                onPinnedChange(false);
                this.toaster.success('Removed from your Custom Dashboard.');
                return true;
              }),
              catchError(() => {
                this.toaster.error('Could not remove this section. Please try again.');
                return of(null);
              }),
            );
        }),
        catchError(() => of(null)),
        finalize(() => onPendingChange(false)),
      )
      .subscribe();
  }

  /**
   * Resolves the catalog `widgets.id` for the `widgetKey` on the given
   * `pageId`, scoped to the active product when possible.
   *
   * Why scope by product: the same `page.id` is unique in `/catalog/products`,
   * but the **same page name** ("Social Media Performance") commonly appears
   * under multiple products with different `page.id`s and different
   * `widgets[].id`s (e.g. Denefits → pageId=9 → widgetIds [57,58,59];
   * Cool Credit → pageId=12 → widgetIds […]). When a caller passes an
   * `expectedProductId`, we restrict the search to that product's pages so
   * we always emit the widget id that belongs to the user's active product
   * — never a `widgets.id` borrowed from another product's catalog entry.
   *
   * Caching is keyed on `pageId+widgetKey`. Because `pageId` is already
   * unique per product page, two products can never collide in the cache.
   */
  private resolveCatalogWidgetId(
    pageId: number,
    widgetKey: string,
    expectedProductId?: string | number | null,
  ): Observable<number | null> {
    const cached = this.widgetIdByPageAndKey.get(pageId)?.get(widgetKey);
    if (cached != null) return of(cached);

    return this.layoutService.getCatalogProducts().pipe(
      map((catalog) => { 
        const scopedCatalog =
          expectedProductId != null && String(expectedProductId).trim() !== ''
            ? (catalog as any[]).filter(
                (p: any) => String(p?.id) === String(expectedProductId),
              )
            : (catalog as any[]); 
        const id = resolveFromCatalog(scopedCatalog, pageId, widgetKey);
        if (id != null) {
          this.cacheWidgetId(pageId, widgetKey, id);
          return id;
        }

        if (scopedCatalog !== catalog) {
          const fallbackId = resolveFromCatalog(catalog as any[], pageId, widgetKey);
          if (fallbackId != null) {
            // eslint-disable-next-line no-console
            console.warn(
              `[CustomDashboardPin] widget_key "${widgetKey}" not found under ` +
                `active product (id=${expectedProductId}) for pageId=${pageId}; ` +
                `falling back to cross-product catalog match (widgetId=${fallbackId}). ` +
                `If this isn't the correct product's widget, verify the catalog ` +
                `entry for product ${expectedProductId}.`,
            );
            this.cacheWidgetId(pageId, widgetKey, fallbackId);
            return fallbackId;
          }
        }

        warnUnresolved(catalog as any[], pageId, widgetKey, expectedProductId);
        return null;
      }),
      catchError(() => of(null)),
    );
  }

  private cacheWidgetId(pageId: number, widgetKey: string, id: number): void {
    let pageMap = this.widgetIdByPageAndKey.get(pageId);
    if (!pageMap) {
      pageMap = new Map();
      this.widgetIdByPageAndKey.set(pageId, pageMap);
    }
    pageMap.set(widgetKey, id);
  }

  private resolveProductId(): string | number | null {
    const pid = this.apiService.selectedProductPid();
    if (pid != null && /^\d+$/.test(String(pid))) return pid;

    const selectedId = this.apiService.selectedProductId();
    if (selectedId == null) return null;

    const product = (this.apiService.products() ?? []).find(
      (p: any) => String(p?.id) === String(selectedId),
    );
    if (product?.pid != null) return product.pid;

    return selectedId;
  }
}

/**
 * Walks `catalog` looking for the widget on `page.id === pageId` whose
 * `widget_key` matches `widgetKey` either exactly or via the
 * product-prefix suffix rule (`endsWith('_' + widgetKey)`). Returns the
 * numeric `widgets.id` or `null` when nothing matches.
 *
 * Stateless on purpose so {@link CustomDashboardPinService.resolveCatalogWidgetId}
 * can run it twice — once scoped to the active product and once across
 * the full catalog as a fallback — without re-reading the catalog.
 */
function resolveFromCatalog(
  catalog: any[],
  pageId: number,
  widgetKey: string,
): number | null {
  for (const product of catalog ?? []) {
    for (const page of product?.pages ?? []) {
      if (Number(page.id) !== pageId) continue;
      // Match priority:
      //   1. Exact (`widget_key === widgetKey`) — backend already canonical
      //   2. Suffix (`widget_key.endsWith('_' + widgetKey)`) — backend
      //      uses a product prefix like `de_`/`co_`/`le_`. Lets the same
      //      section enum (e.g. `sp_overall_stats`) resolve across every
      //      product without hardcoding the prefix in the enum.
      const suffix = '_' + widgetKey;
      const matchedWidget = (page?.widgets ?? []).find(
        (w: any) =>
          w?.widget_key === widgetKey ||
          String(w?.widget_key ?? '').endsWith(suffix),
      );
      if (matchedWidget) {
        const id = Number(matchedWidget.id);
        if (Number.isFinite(id) && id > 0) return id;
      }
    }
  }
  return null;
}

/**
 * Emits a precise console diagnostic explaining why a `widget_key`
 * couldn't be resolved on `pageId`. Distinguishes "no such page" from
 * "page exists but doesn't expose this widget_key" and lists the keys
 * the catalog actually advertises for that page so a dev can align the
 * frontend section id with the backend in one glance.
 */
function warnUnresolved(
  catalog: any[],
  pageId: number,
  widgetKey: string,
  expectedProductId: string | number | null | undefined,
): void {
  let matchedPageName: string | null = null;
  let availableKeys: string[] = [];
  for (const product of catalog ?? []) {
    for (const page of product?.pages ?? []) {
      if (Number(page.id) !== pageId) continue;
      matchedPageName = String(page?.name ?? '');
      availableKeys = (page?.widgets ?? [])
        .map((w: any) => String(w?.widget_key ?? ''))
        .filter(Boolean);
      break;
    }
    if (matchedPageName != null) break;
  }

  if (matchedPageName == null) {
    // eslint-disable-next-line no-console
    console.warn(
      `[CustomDashboardPin] No catalog page found for pageId=${pageId}` +
        (expectedProductId != null ? ` (active product=${expectedProductId})` : '') +
        `. Verify the page is registered in the dashboards catalog for this product.`,
    );
    return;
  }

  // eslint-disable-next-line no-console
  console.warn(
    `[CustomDashboardPin] widget_key "${widgetKey}" not found on ` +
      `catalog page "${matchedPageName}" (id=${pageId})` +
      (expectedProductId != null ? ` for active product=${expectedProductId}` : '') +
      `. Available widget_keys on this page: ` +
      (availableKeys.length ? availableKeys.join(', ') : '(none)') +
      `. Align the backend widget_key with the frontend section id, ` +
      `or update the section id to match the backend.`,
  );
}

/**
 * Strips a leading 2-character product prefix from a backend `widget_key`
 * so frontend section enums can be product-agnostic.
 *   e.g. `de_sp_overall_stats` → `sp_overall_stats`
 *        `co_sp_overall_stats` → `sp_overall_stats`
 *        `sp_overall_stats`    → `sp_overall_stats` (unchanged)
 *
 * Only strips when the first segment is exactly 2 characters so 3+ char
 * non-product prefixes like `mr1_`, `mr2_`, `seo_` survive intact.
 */
function stripProductPrefix(widgetKey: string): string {
  const match = /^[a-z]{2}_(.+)$/i.exec(widgetKey);
  return match ? match[1] : widgetKey;
}
